/**
 * ScoringCardPanel — accordion list of the 5 chosen wildlife scoring cards.
 *
 * Originally derived from the (removed) board-game-playground for visual parity. UI is NOT shared (CLAUDE.md).
 */

import type { ScoringCardSelection } from "@cloudnest/cascadia-engine";
import { WILDLIFE_SCORING_CARDS_BY_ID } from "@cloudnest/cascadia-engine";
import { useState } from "react";
import { PlaceholderCard } from "./placeholders";

export interface ScoringCardPanelProps {
  readonly scoringCards: ScoringCardSelection;
}

const SPECIES_ORDER = ["bear", "elk", "salmon", "hawk", "fox"] as const;

export function ScoringCardPanel({ scoringCards }: ScoringCardPanelProps) {
  const [openSpecies, setOpenSpecies] = useState<string | null>(null);

  return (
    <section
      data-testid="cascadia-scoring-cards"
      aria-label="Scoring cards"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        background: "var(--cn-cascadia-tile-bg)",
        border: "1px solid var(--cn-cascadia-market-slot-border)",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "8px 12px",
          fontSize: 11,
          fontWeight: 600,
          color: "var(--cn-cascadia-label)",
          background: "var(--cn-cascadia-market-slot)",
        }}
      >
        Scoring Cards
      </div>

      {SPECIES_ORDER.map((species) => {
        const cardId = scoringCards[species];
        const card = WILDLIFE_SCORING_CARDS_BY_ID[cardId];
        const isOpen = openSpecies === species;

        return (
          <div key={species}>
            <button
              data-testid={`cascadia-scoring-card-row-${species}`}
              onClick={() => setOpenSpecies(isOpen ? null : species)}
              type="button"
              aria-expanded={isOpen}
              style={{
                width: "100%",
                padding: "8px 12px",
                background: isOpen
                  ? "var(--cn-cascadia-phase-bg)"
                  : "transparent",
                border: "none",
                borderTop: "1px solid var(--cn-cascadia-tray-empty-slot)",
                cursor: "pointer",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                minHeight: 36,
                color: "var(--cn-cascadia-label)",
                fontSize: 12,
              }}
            >
              <span>
                {species.charAt(0).toUpperCase() + species.slice(1)} — {cardId}
              </span>
              <span aria-hidden="true">{isOpen ? "▾" : "▸"}</span>
            </button>

            {isOpen && card ? (
              <div style={{ padding: 12 }}>
                <svg width={200} height={200} overflow="visible">
                  <PlaceholderCard kind="wildlife" card={card} width={200} />
                </svg>
              </div>
            ) : null}
          </div>
        );
      })}
    </section>
  );
}
